import { GripVertical } from 'lucide-react';
import { lazy, Suspense, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './SortableList.module.css';
import type { DragHandleProps, SortableListProps } from './types';

export type { DragHandleProps, SortableListProps } from './types';
export { moveItem } from './move';

/**
 * Loaded on demand: `@dnd-kit` is a quarter of the initial JS and dragging is rare.
 *
 * `lazy()` erases the generic — it types the result as a component over one fixed prop
 * type — so it's cast back to the generic signature the real module exports. The cast
 * is safe because that module's props *are* `SortableListProps<T>`.
 */
const SortableListDnd = lazy(() => import('./SortableListDnd')) as unknown as <
  T extends { id: string },
>(
  props: SortableListProps<T>,
) => ReactNode;

/**
 * Drag to reorder. Touch: long-press the handle (250ms). Mouse: drag the handle.
 * Keyboard: focus the handle, Space to lift, arrows to move, Space to drop.
 *
 * The list renders **immediately** as a plain list, with the drag machinery arriving a
 * moment later (doc 05 › Performance budget). Nothing is missing in the meantime: the
 * rows are all there and readable, and "Move up / Move down" in each row's menu doesn't
 * depend on dragging at all — which is also the keyboard-free path this has always had.
 */
export function SortableList<T extends { id: string }>(props: SortableListProps<T>) {
  return (
    <Suspense fallback={<StaticList {...props} />}>
      <SortableListDnd {...props} />
    </Suspense>
  );
}

/** The same rows, without dragging: what's on screen until the drag chunk lands. */
function StaticList<T extends { id: string }>({
  items,
  renderItem,
  label,
  className,
}: SortableListProps<T>) {
  return (
    <ul className={cx(s.list, className)} aria-label={label}>
      {items.map((item) => (
        <li key={item.id} className={s.row}>
          {/* A handle that isn't draggable yet, rather than a hole where one goes. */}
          {renderItem(item, { handle: {}, isDragging: false })}
        </li>
      ))}
    </ul>
  );
}

/** The grab handle. Needs a specific label, e.g. "Reorder LEGO Botanical Orchid". */
export function DragHandle({ label, ...handle }: DragHandleProps & { label: string }) {
  return (
    <button type="button" className={s.handle} aria-label={label} {...handle}>
      <Icon icon={GripVertical} size="md" />
    </button>
  );
}
